import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Clock, LayoutList, PauseCircle, SquareKanban } from 'lucide-react';
import {
  CRM_PROJECT_STATUS, CRM_STAGE, PRIORITY, dateLabel, type CrmProjectStatus, type CrmStage, type Priority,
} from '@adisys/shared';
import { api } from '../../lib/api';
import { MetaBadge, StageBadge, inr } from '../../lib/crm';
import { useCrmMeta } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Column, DataTable, FilterBar, FilterSelect, SearchInput } from '../../components/DataTable';
import { Card, ErrorState, Skeleton, StatusBadge, cx } from '../../components/ui';

type View = 'board' | 'list';
const VIEW_KEY = 'adisys.crm.projects.view';

function readView(): View {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'board'; } catch { return 'board'; }
}

const daysSince = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));

function PriorityBadge({ value }: { value: Priority }) {
  const m = PRIORITY.byValue[value];
  return <StatusBadge tone={m.tone} dot={false} title={m.description}>{m.label}</StatusBadge>;
}

function ProjectCard({ p, onOpen }: { p: any; onOpen: () => void }) {
  const days = daysSince(p.stageEnteredAt);
  const stale = p.status === 'active' && days > 7;
  return (
    <button onClick={onOpen}
      className="w-full rounded-lg bg-card p-3 text-left shadow-card ring-1 ring-line transition-shadow hover:shadow-raised">
      <div className="flex items-start justify-between gap-2">
        <p className="tabular text-xs font-medium text-ink-500">{p.projectCode}</p>
        <PriorityBadge value={p.priority} />
      </div>
      <p className="mt-1 line-clamp-2 text-sm font-medium text-ink-900">{p.name}</p>
      <p className="mt-0.5 truncate text-xs text-ink-500">{p.customerName}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="tabular text-sm font-semibold text-ink-900">{inr(p.value, true)}</span>
        <span className={cx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px]',
          stale ? 'bg-warning-soft text-warning' : 'bg-ink-100 text-ink-500')}
          title="Days in this stage">
          <Clock className="h-3 w-3" aria-hidden />{days}d
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-ink-500">
        <span className="truncate">{p.salesExecutiveName ?? 'No sales executive'}</span>
        {p.status === 'on_hold' && (
          <span className="inline-flex items-center gap-1 text-warning"><PauseCircle className="h-3.5 w-3.5" aria-hidden />On hold</span>
        )}
      </div>
    </button>
  );
}

function Board() {
  const navigate = useNavigate();
  const board = useQuery({ queryKey: ['crm', 'projects', 'board'], queryFn: () => api.get('/crm/projects/board') });

  if (board.isLoading) {
    return (
      <div className="flex flex-col gap-3 md:flex-row md:overflow-x-auto">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 w-full md:w-72 md:shrink-0" />)}
      </div>
    );
  }
  if (board.isError) return <Card><ErrorState error={board.error} onRetry={() => board.refetch()} /></Card>;

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:overflow-x-auto md:pb-3">
      {board.data.columns.map((col: any) => (
        <section key={col.stage}
          className="w-full rounded-card bg-sunken/70 p-2.5 ring-1 ring-line md:w-72 md:shrink-0">
          <header className="mb-2 flex items-start justify-between gap-2 px-1">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-ink-900">{col.label}</h2>
              <p className="text-xs text-ink-500">{col.owner}</p>
            </div>
            <div className="text-right">
              <p className="tabular text-sm font-semibold text-ink-900">{col.count}</p>
              <p className="tabular text-xs text-ink-500">{inr(col.value, true)}</p>
            </div>
          </header>
          <div className="space-y-2">
            {col.cards.length === 0
              ? <p className="rounded-lg border border-dashed border-line-strong px-3 py-5 text-center text-xs text-ink-400">Nothing here</p>
              : col.cards.map((p: any) => <ProjectCard key={p.id} p={p} onOpen={() => navigate(`/crm/projects/${p.id}`)} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function List() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const meta = useCrmMeta();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [exec, setExec] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });
  const stage = params.get('stage') ?? '';

  const setStage = (v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set('stage', v); else next.delete('stage');
    setParams(next, { replace: true });
    setPage(1);
  };

  const q = useQuery({
    queryKey: ['crm', 'projects', 'list', { search, stage, status, priority, exec, page, sort }],
    queryFn: () => api.get('/crm/projects', {
      q: search || undefined, stage: stage || undefined, status: status || undefined,
      priority: priority || undefined, salesExecutiveId: exec || undefined,
      page, size: 25, sort: sort.key, dir: sort.dir,
    }),
  });

  const columns: Array<Column<any>> = [
    {
      key: 'name', header: 'Project', sortable: true,
      render: (p) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{p.name}</p>
          <p className="tabular truncate text-xs text-ink-500">{p.projectCode} · {p.customerName}</p>
        </div>
      ),
    },
    { key: 'stage', header: 'Stage', render: (p) => <StageBadge stage={p.stage as CrmStage} /> },
    { key: 'status', header: 'Status', hideBelow: 'md', render: (p) => <MetaBadge meta={CRM_PROJECT_STATUS} value={p.status as CrmProjectStatus} /> },
    { key: 'priority', header: 'Priority', hideBelow: 'lg', render: (p) => <PriorityBadge value={p.priority} /> },
    { key: 'sales', header: 'Sales', hideBelow: 'lg', render: (p) => p.salesExecutiveName ?? <span className="text-ink-400">—</span> },
    { key: 'value', header: 'Value', numeric: true, render: (p) => <span className="font-medium text-ink-900">{inr(p.value, true)}</span> },
    { key: 'expectedCloseDate', header: 'Expected close', sortable: true, hideBelow: 'xl', render: (p) => dateLabel(p.expectedCloseDate) },
    { key: 'stageEnteredAt', header: 'In stage', numeric: true, sortable: true, hideBelow: 'xl', render: (p) => `${daysSince(p.stageEnteredAt)}d` },
  ];

  const active = [search, stage, status, priority, exec].filter(Boolean).length;
  return (
    <DataTable mobile="cards"
      columns={columns} rows={q.data?.data ?? []} rowKey={(p) => p.id}
      onRowClick={(p) => navigate(`/crm/projects/${p.id}`)}
      loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
      sort={sort} onSort={(key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}
      page={q.data?.page} onPage={setPage}
      empty={{ icon: <Briefcase className="h-5 w-5" />, title: active ? 'No projects match these filters' : 'No projects yet',
        description: 'Projects are created by converting a qualified lead.' }}
      toolbar={
        <FilterBar active={active} onReset={() => { setSearch(''); setStatus(''); setPriority(''); setExec(''); setStage(''); }}>
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Project, code or customer" />
          <FilterSelect label="Stage" value={stage} onChange={setStage} allLabel="All stages"
            options={CRM_STAGE.list.map((s) => ({ value: s.value, label: s.label }))} />
          <FilterSelect label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} allLabel="All statuses"
            options={CRM_PROJECT_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} />
          <FilterSelect label="Priority" value={priority} onChange={(v) => { setPriority(v); setPage(1); }} allLabel="All priorities"
            options={PRIORITY.list.map((s) => ({ value: s.value, label: s.label }))} />
          <FilterSelect label="Sales executive" value={exec} onChange={(v) => { setExec(v); setPage(1); }} allLabel="All sales executives"
            options={(meta.data?.people?.sales ?? []).map((u: any) => ({ value: u.id, label: u.fullName }))} />
        </FilterBar>
      }
    />
  );
}

export function CrmProjectsPage() {
  const [view, setView] = useState<View>(readView);
  const choose = (v: View) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* not fatal */ } };

  const toggle = (
    <div className="inline-flex rounded-lg bg-ink-100 p-0.5" role="group" aria-label="View">
      {([['board', 'Board', SquareKanban], ['list', 'List', LayoutList]] as const).map(([v, label, Icon]) => (
        <button key={v} onClick={() => choose(v)} aria-pressed={view === v}
          className={cx('inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium',
            view === v ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-800')}>
          <Icon className="h-3.5 w-3.5" aria-hidden />{label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <PageHeader title="Project pipeline"
        description="Every deal from site survey to handover. Projects move forward as each team completes its step."
        actions={toggle} />
      <div className="p-4 sm:p-6">{view === 'board' ? <Board /> : <List />}</div>
    </>
  );
}
